import type { BoxProps } from '@mui/material/Box';
import Box from '@mui/material/Box';
import { styled } from '@mui/system';
import is from '@sindresorhus/is';
import type { FunctionComponent, PropsWithChildren, ReactNode } from 'react';

import { SpacingMultiple } from '../../../design/components/constants';
import { Headline } from '../Typography';

const StyledContentWrapperBase = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette['background'].default,
    borderColor: theme.palette['cardborder'].main,
    borderRadius: '8px',
    borderStyle: 'solid',
    borderWidth: 1,
    overflow: 'hidden',
    width: '100%',
}));

export type ContentWrapperProps = {
    readonly backgroundColor?: string;
    readonly overflow?: BoxProps['overflow'];
};

export const ContentWrapper: FunctionComponent<PropsWithChildren<ContentWrapperProps>> = (props) => {
    const { backgroundColor: backgroundColorOverride, children, overflow: overflowOverride } = props;

    const style: BoxProps['sx'] = {
        backgroundColor: backgroundColorOverride ?? null,
        overflow: overflowOverride ?? null,
    };

    return <StyledContentWrapperBase sx={{ ...style }}>{children}</StyledContentWrapperBase>;
};

type ContentDetailsProps = PropsWithChildren;

export const ContentDetails: FunctionComponent<ContentDetailsProps> = ({ children }) => (
    <Box p={SpacingMultiple.large}>{children}</Box>
);

type ContentHeaderProps = PropsWithChildren<{ readonly noSpacing?: boolean }>;

export const ContentHeader: FunctionComponent<ContentHeaderProps> = ({ children, noSpacing }) => (
    <Box
        sx={{
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'cardborder.main',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            minHeight: noSpacing === true ? 'auto' : '52px',
            paddingInline: noSpacing === true ? 0 : SpacingMultiple.large,
        }}
    >
        {children}
    </Box>
);

type ContentHeadlineProps = { readonly actions?: ReactNode; readonly text: string };

export const ContentHeadline: FunctionComponent<ContentHeadlineProps> = ({ actions, text }) => (
    <ContentHeader>
        <Headline variant="h5">{text}</Headline>
        {is.undefined(actions) === false && actions}
    </ContentHeader>
);
